import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Loader2, FileUp, Save, Download } from "lucide-react";
import { SectionHeader } from "@/components/common/Widgets";
import ContextualUploader from "@/components/common/ContextualUploader";
import { fmtNum } from "@/lib/format";

const BS_FIELDS = [
  { key: "capital_networth", label: "Capital / Net Worth" },
  { key: "reserves_surplus", label: "Reserves & Surplus" },
  { key: "long_term_borrowings", label: "Long Term Borrowings" },
  { key: "short_term_borrowings", label: "Short Term Borrowings (CC/OD)" },
  { key: "sundry_creditors", label: "Sundry Creditors" },
  { key: "other_current_liabilities", label: "Other Current Liabilities" },
  { key: "provisions", label: "Provisions" },
  { key: "total_liabilities", label: "TOTAL LIABILITIES" },
  { key: "net_fixed_assets", label: "Net Fixed Assets" },
  { key: "capital_wip", label: "Capital WIP" },
  { key: "investments", label: "Investments" },
  { key: "inventory", label: "Inventory" },
  { key: "sundry_debtors", label: "Sundry Debtors" },
  { key: "cash_bank", label: "Cash & Bank" },
  { key: "other_current_assets", label: "Other Current Assets" },
  { key: "total_assets", label: "TOTAL ASSETS" },
];
const PL_FIELDS = [
  { key: "net_sales", label: "Net Sales / Turnover" },
  { key: "other_income", label: "Other Income" },
  { key: "cogs", label: "Cost of Goods Sold" },
  { key: "employee_cost", label: "Employee Cost" },
  { key: "selling_admin", label: "Selling & Admin" },
  { key: "depreciation", label: "Depreciation" },
  { key: "interest_tl", label: "Interest on Term Loan" },
  { key: "interest_wc", label: "Interest on WC" },
  { key: "profit_before_tax", label: "Profit Before Tax" },
  { key: "tax", label: "Tax" },
  { key: "profit_after_tax", label: "Profit After Tax" },
];
const RATIOS = [
  { key: "current_ratio", label: "Current Ratio" },
  { key: "quick_ratio", label: "Quick Ratio" },
  { key: "debt_equity", label: "Debt/Equity" },
  { key: "operating_margin", label: "Operating Margin (%)" },
  { key: "net_profit_margin", label: "Net Profit Margin (%)" },
  { key: "interest_coverage", label: "Interest Coverage" },
  { key: "dscr", label: "DSCR" },
  { key: "tnw", label: "TNW (₹ L)" },
  { key: "wc_gap", label: "WC Gap (₹ L)" },
];

function makeEmpty(n) { return Array(n).fill(null); }

export default function CMASection({ app, cma, reload }) {
  const { t } = useTranslation();
  const periods = cma?.periods?.length ? cma.periods : ["FY23-A", "FY24-A", "FY25-E", "FY26-P"];
  const n = periods.length;
  const [bs, setBs] = useState(() => Object.fromEntries(BS_FIELDS.map(f => [f.key, (cma?.balance_sheet?.[f.key]?.length === n) ? cma.balance_sheet[f.key] : makeEmpty(n)])));
  const [pl, setPl] = useState(() => Object.fromEntries(PL_FIELDS.map(f => [f.key, (cma?.profit_loss?.[f.key]?.length === n) ? cma.profit_loss[f.key] : makeEmpty(n)])));
  const [ratios, setRatios] = useState(cma?.ratios || {});
  const [mpbf, setMpbf] = useState(cma?.mpbf || null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [extractText, setExtractText] = useState("");
  const [extractFile, setExtractFile] = useState(null);
  const [latestExtraction, setLatestExtraction] = useState(null);

  useEffect(() => {
    if (cma) {
      setBs(Object.fromEntries(BS_FIELDS.map(f => [f.key, (cma?.balance_sheet?.[f.key]?.length === n) ? cma.balance_sheet[f.key] : makeEmpty(n)])));
      setPl(Object.fromEntries(PL_FIELDS.map(f => [f.key, (cma?.profit_loss?.[f.key]?.length === n) ? cma.profit_loss[f.key] : makeEmpty(n)])));
      setRatios(cma?.ratios || {});
      setMpbf(cma?.mpbf || null);
    }
  // eslint-disable-next-line
  }, [cma]);

  const updateCell = (section, key, i, val) => {
    const v = val === "" ? null : Number(val);
    if (section === "bs") setBs((s) => ({ ...s, [key]: s[key].map((x, idx) => (idx === i ? v : x)) }));
    else setPl((s) => ({ ...s, [key]: s[key].map((x, idx) => (idx === i ? v : x)) }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post("/cma", { application_id: app.id, periods, balance_sheet: bs, profit_loss: pl });
      setRatios(res.data.ratios || {});
      setMpbf(res.data.mpbf);
      toast.success("CMA saved and ratios recomputed");
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const [extractProgress, setExtractProgress] = useState({ active: false, pct: 0, status: null });
  const runAIExtract = async () => {
    if (!extractText && !extractFile) { toast.error("Paste text or upload a PDF first"); return; }
    setAiBusy(true);
    setExtractProgress({ active: true, pct: 5, status: "QUEUED" });
    try {
      let document_id = null;
      if (extractFile) {
        const fd = new FormData();
        fd.append("application_id", app.id);
        fd.append("category", "FINANCIAL");
        fd.append("file", extractFile);
        const up = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        document_id = up.data.id;
        setExtractProgress({ active: true, pct: 15, status: "UPLOADED" });
      }
      // Kick off async job
      const start = await api.post("/ai/extract-financials/async", { application_id: app.id, document_id, inline_text: extractText || null });
      const jobId = start.data.job_id;
      let tick = 20;
      const { pollJob } = await import("@/lib/jobs");
      const final = await pollJob(jobId, {
        intervalMs: 2500,
        onProgress: (j) => {
          if (j.status === "RUNNING") { tick = Math.min(90, tick + 8); setExtractProgress({ active: true, pct: tick, status: j.status }); }
          else if (j.status === "RETRYING") { setExtractProgress({ active: true, pct: 35, status: "RETRYING" }); }
          else { setExtractProgress((p) => ({ ...p, status: j.status })); }
        },
      });
      if (final.status === "SUCCESS") {
        // Fetch the resulting extraction record
        const extId = final.result_ref;
        const list = await api.get(`/ai/extractions/${app.id}`);
        const ext = (list.data || []).find((x) => x.id === extId) || (list.data || [])[0];
        setLatestExtraction(ext);
        setExtractProgress({ active: true, pct: 100, status: "SUCCESS" });
        toast.success("Auto-extraction complete — review and accept to apply to CMA");
      } else {
        throw new Error(final.error || "Extraction job failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Extraction failed");
    } finally {
      setAiBusy(false);
      setTimeout(() => setExtractProgress({ active: false, pct: 0, status: null }), 1200);
    }
  };

  const acceptExtraction = async () => {
    if (!latestExtraction) return;
    setAiBusy(true);
    try {
      const res = await api.post("/ai/accept-extraction", { application_id: app.id, extraction_id: latestExtraction.id });
      toast.success("Extraction applied to CMA");
      reload && reload();
      setLatestExtraction(null);
      setExtractText("");
      setExtractFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally { setAiBusy(false); }
  };

  const downloadXlsx = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("acas_token");
    try {
      const r = await fetch(`${BACKEND_URL}/api/exports/cma/${app.id}.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("download");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `CMA_${app.ref_no}.xlsx`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-3">
      {cma?.locked && (
        <div className="p-3 rounded-md border bg-warning/10 border-warning/30 text-warning text-sm flex items-center gap-2" data-testid="cma-locked-banner">
          <span className="font-semibold">🔒 CMA Locked</span>
          <span className="text-warning/80">Version {cma.version} \u2014 finalized by {cma.locked_by}. Submit \"CMA new version\" via Maker-Checker to edit.</span>
        </div>
      )}
      <SectionHeader title={t("tabs.cmaTitle", "CMA Builder")} subtitle={t("tabs.cmaSubtitle", "Edit financial line-items (₹ Lakhs) and recompute ratios.")} action={(
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadXlsx} disabled={!cma} data-testid="cma-download-button"><Download className="h-4 w-4 mr-1" />{t("tabs.cmaDownload", "Download CMA")}</Button>
          <Button onClick={save} disabled={busy || cma?.locked} data-testid="cma-save-button"><Save className="h-4 w-4 mr-1" />{busy ? t("app.loading", "Saving…") : t("tabs.cmaSave", "Save & Recompute")}</Button>
        </div>
      )} />

      <Card className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            <h3 className="text-sm font-semibold heading">Auto-Fill from Financial Document</h3>
            <Badge variant="outline" className="text-[10px]">Gemini 2.5 Pro</Badge>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Textarea rows={5} value={extractText} onChange={(e) => setExtractText(e.target.value)} placeholder="Paste audit report / balance sheet text here, or upload a PDF below…" data-testid="cma-extract-text" />
          <div className="space-y-2">
            <label className="flex items-center gap-2 rounded-md border-dashed border-2 p-3 cursor-pointer hover:bg-muted/40">
              <FileUp className="h-4 w-4 text-muted-foreground" />
              <input type="file" accept=".pdf,.txt,.csv" onChange={(e) => setExtractFile(e.target.files?.[0] || null)} className="hidden" data-testid="cma-extract-file" />
              <span className="text-sm text-muted-foreground">{extractFile ? extractFile.name : "Choose a PDF / TXT / CSV…"}</span>
            </label>
            <div className="flex gap-2">
              <Button onClick={runAIExtract} disabled={aiBusy} data-testid="cma-extract-button">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{aiBusy ? "Extracting…" : "Auto Extract"}</Button>
              {latestExtraction && <Button variant="outline" onClick={acceptExtraction} disabled={aiBusy} data-testid="cma-extract-accept">Accept & apply to CMA</Button>}
            </div>
            {extractProgress.active && (
              <div className="rounded-md border border-accent/30 bg-accent/5 p-2.5 space-y-1.5">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-medium flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin text-accent" />Extracting financials… {extractProgress.status && <Badge variant="outline" className="text-[10px] ml-1" data-testid="cma-extract-job-status">{extractProgress.status}</Badge>}</span>
                  <span className="mono text-muted-foreground">{extractProgress.pct}%</span>
                </div>
                <Progress value={extractProgress.pct} className="h-1" />
              </div>
            )}
            {latestExtraction && (
              <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2 max-h-32 overflow-auto mono">
                Periods: {(latestExtraction.result?.periods || []).join(", ")}<br/>
                Borrower: {latestExtraction.result?.borrower?.name}<br/>
                Net Sales: {(latestExtraction.result?.profit_loss?.net_sales || []).join(", ")}
              </div>
            )}
            <ContextualUploader applicationId={app.id} category="FINANCIAL" title="Attach more financial statements (ITR/Audit)" multiple compact />
          </div>
        </div>
      </Card>

      <FinancialGrid title="BALANCE SHEET (₹ Lakhs)" rows={BS_FIELDS} data={bs} periods={periods} onChange={(k, i, v) => updateCell("bs", k, i, v)} />
      <FinancialGrid title="PROFIT & LOSS (₹ Lakhs)" rows={PL_FIELDS} data={pl} periods={periods} onChange={(k, i, v) => updateCell("pl", k, i, v)} />

      <Card className="p-4">
        <SectionHeader title="Computed Ratios & DSCR" subtitle="Read-only — recomputed on save." />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Metric</th>{periods.map(p => <th key={p} className="text-right px-3 py-2 mono">{p}</th>)}</tr>
            </thead>
            <tbody>
              {RATIOS.map(r => (
                <tr key={r.key} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{r.label}</td>
                  {periods.map((_, i) => <td key={i} className="px-3 py-2 num-right">{fmtNum(ratios?.[r.key]?.[i] ?? null, 2)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {mpbf && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="MPBF (Tandon II)" v={`₹ ${fmtNum(mpbf.tandon_method2)} L`} />
            <Stat label="MPBF (Nayak)" v={`₹ ${fmtNum(mpbf.nayak_method)} L`} />
            <Stat label="Projected T/O" v={`₹ ${fmtNum(mpbf.projected_turnover)} L`} />
            <Stat label="WC Gap" v={`₹ ${fmtNum(mpbf.working_capital_gap)} L`} />
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="rounded-md border p-3 bg-muted/30">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
      <div className="mt-0.5 text-base font-semibold mono tabular">{v}</div>
    </div>
  );
}

function FinancialGrid({ title, rows, data, periods, onChange }) {
  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-muted/40 text-sm font-semibold uppercase tracking-wide">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-muted/30 z-10 min-w-[230px]">Line Item</th>
              {periods.map(p => <th key={p} className="text-right px-3 py-2 mono w-[120px]">{p}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-border hover:bg-muted/20">
                <td className="px-3 py-1.5 sticky left-0 bg-card font-medium">{r.label}</td>
                {periods.map((_, i) => (
                  <td key={i} className="px-2 py-1">
                    <Input type="number" step="0.01" value={data[r.key]?.[i] ?? ""} onChange={(e) => onChange(r.key, i, e.target.value)} className="h-8 text-right mono text-sm" data-testid={`cma-cell-${r.key}-${i}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
